import React, { useEffect, useRef } from 'react';

import { ControlPosition } from 'src/components/map/GoogleMap';
import GoogleMapControl from 'src/components/map/GoogleMapControl';
import { GoogleMapDrawingControlOption } from 'src/components/map/GoogleMapDrawingControlOption';
import { FullscreenMapHeader } from 'src/components/map/FullscreenMapHeader';
import { useMapDrawing } from '../hooks/useMapDrawing';
import useUser from 'src/hooks/useUser';
import {
    fitMapToWoopenGeometry,
    transformGoogleMapBoundsToGeometry,
    transformGoogleMapPolygonToGeometry,
} from 'src/utils/googleMapUtils';
import type { GeometryInput } from 'src/types/__generated__/graphql';
import { GeometrySource } from 'src/types/__generated__/graphql';
import { useAdSearchState } from '../contexts/AdSearchStateContext';

type Props = {
    map: google.maps.Map;
    isDrawingMode: boolean;
    setIsDrawingMode: React.Dispatch<React.SetStateAction<boolean>>;
};

export const SearchAdsPageMapDrawing: React.FC<Props> = ({ map, isDrawingMode, setIsDrawingMode }) => {
    const user = useUser();
    const {
        drawingError,
        drawnPolygon,
        isDrawnPolygonValid,
        drawingManager,
        setDrawingManager,
        drawOverlayPolygonFromDrawnPolygon,
        clearCurrentPolygon,
        transformQueryVariablesPlaceToDrawnPolygon,
        completePolygon,
        clearPolygonAndActivateDrawing,
        clearDrawingWithoutExitDrawingMode,
        exitDrawingMode,
    } = useMapDrawing({ map, setIsDrawingMode });

    useDrawingOnQueryVariablesChange({
        drawingManager,
        clearCurrentPolygon,
        transformQueryVariablesPlaceToDrawnPolygon,
        drawOverlayPolygonFromDrawnPolygon,
    });

    const { adsQueryVariables, changeAdSearchState, isMapFullscreen, changeMapFullscreen } = useAdSearchState();

    const clearDrawingAndResetPlaces = async () => {
        clearCurrentPolygon();
        const mapBounds = map.getBounds();
        map.moveCamera({ center: mapBounds?.getCenter() }); // Strangely, if we don't move the camera, the markers on the map are not updated
        if (mapBounds) {
            // We transform the map box in a "polygon" to have only ads in this box
            const mapBoundsGeometryInput = transformGoogleMapBoundsToGeometry(mapBounds);
            await changeAdSearchState({
                adsQueryVariables: {
                    ...adsQueryVariables,
                    places: [mapBoundsGeometryInput],
                    placesSource: GeometrySource.Viewport,
                    place: undefined,
                },
                isPolygonDisplayed: false,
            });
        }
    };

    const onSearchClick = async () => {
        if (isDrawnPolygonValid && drawnPolygon) {
            const polygonGeometry = transformGoogleMapPolygonToGeometry(drawnPolygon);
            await changeAdSearchState({
                adsQueryVariables: {
                    ...adsQueryVariables,
                    places: [polygonGeometry],
                    placesSource: GeometrySource.Drawing,
                },
                isPolygonDisplayed: true,
                fitMapOnAdSearchStateChange: true,
            });
            drawnPolygon.setMap(null);

            fitMapToWoopenGeometry(map, polygonGeometry, 10);
            exitDrawingMode();
            changeMapFullscreen(false);
        }
    };

    const onDrawClick = () => {
        clearPolygonAndActivateDrawing();

        setIsDrawingMode(true);
        changeMapFullscreen(true);
    };

    const onCloseFullscreenMapClick = async () => {
        // We want to clear and reset the places when we close the fullscreen map only when we are in drawing mode
        // Not when the user just wants to see the map in fullscreen
        if (isDrawingMode) {
            await clearDrawingAndResetPlaces();
        }

        exitDrawingMode();
        changeMapFullscreen(false);
    };

    useExitFullscreenEscapeKey({
        isMapFullscreen,
        isDrawingMode,
        clearDrawingAndResetPlaces,
        exitDrawingMode,
    });

    return (
        <GoogleMapControl
            containerId="DrawPolygonControl"
            position={ControlPosition.TOP_LEFT}
            zIndex={20}
            map={map}
            height="4rem"
        >
            {isMapFullscreen && (
                <FullscreenMapHeader
                    isProApp={Boolean(user?.isProfessionnal)}
                    onCloseClick={onCloseFullscreenMapClick}
                />
            )}
            <GoogleMapDrawingControlOption
                isDrawingMode={isDrawingMode}
                hasDrawnPolygon={Boolean(drawnPolygon)}
                isDrawnPolygonValid={isDrawnPolygonValid}
                drawingError={drawingError}
                onClearClick={clearDrawingWithoutExitDrawingMode}
                onSearchClick={onSearchClick}
                onDrawClick={onDrawClick}
                onDeleteZoneClick={clearDrawingAndResetPlaces}
                onPolygonComplete={completePolygon}
                onDrawingManagerLoad={setDrawingManager}
            />
        </GoogleMapControl>
    );
};

type UseDrawingOnQueryVariablesChangeParams = {
    drawingManager: google.maps.drawing.DrawingManager | null;
    clearCurrentPolygon: () => void;
    transformQueryVariablesPlaceToDrawnPolygon: (place: GeometryInput) => google.maps.Polygon;
    drawOverlayPolygonFromDrawnPolygon: (drawnPoly: google.maps.Polygon) => void;
};
/**
 * Hooks that draw or clear the polygon on the map when the query variables change.
 */
export const useDrawingOnQueryVariablesChange = ({
    drawingManager,
    clearCurrentPolygon,
    transformQueryVariablesPlaceToDrawnPolygon,
    drawOverlayPolygonFromDrawnPolygon,
}: UseDrawingOnQueryVariablesChangeParams) => {
    const { isPolygonDisplayed, adsQueryVariables } = useAdSearchState();

    useEffect(() => {
        if (adsQueryVariables.places?.[0] && isPolygonDisplayed) {
            clearCurrentPolygon();
            const drawnPolygonTemp = transformQueryVariablesPlaceToDrawnPolygon(adsQueryVariables.places[0]);
            drawOverlayPolygonFromDrawnPolygon(drawnPolygonTemp);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adsQueryVariables.places, isPolygonDisplayed]);

    useEffect(() => {
        if (!isPolygonDisplayed) {
            clearCurrentPolygon();
            drawingManager?.setMap(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPolygonDisplayed, drawingManager]);
};

type UseExitFullscreenEscapeKeyParams = {
    isMapFullscreen: boolean;
    isDrawingMode: boolean;
    clearDrawingAndResetPlaces: () => void;
    exitDrawingMode: () => void;
};

/**
 * Hooks that detects when the user exits the fullscreen mode of the map by clicking on the escape key and exits the drawing mode if it is active.
 */
export const useExitFullscreenEscapeKey = ({
    isMapFullscreen,
    isDrawingMode,
    clearDrawingAndResetPlaces,
    exitDrawingMode,
}: UseExitFullscreenEscapeKeyParams) => {
    // If the user is in drawing mode and exits the fullscreen map mode by clicking on the escape key (which is a feature of the browser) and not on the close button, we need to detect it and exit the drawing mode
    // We store the last fullscreen element to compare it with the current one and detect when the user exits the fullscreen mode of the map and not something else
    // I really looked for a better way to do it but I didn't find anything else that works 100% of the time
    const lastFullscreenElementWasMap = useRef(false);
    useEffect(() => {
        if (!isMapFullscreen && isDrawingMode && lastFullscreenElementWasMap.current) {
            clearDrawingAndResetPlaces();
            exitDrawingMode();
        }

        // There is no id or name we can use to know if the fullscreen element is the map. The better solution I found
        // is to look if one of the children of the fullscreen element is the map element with the class 'gm-style'
        const mapElement = document.fullscreenElement?.querySelector(':scope .gm-style');
        lastFullscreenElementWasMap.current = Boolean(mapElement);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapFullscreen]);
};
